import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { waitlistAPI } from '../services/api';
import './TournamentWaitlist.css';

/**
 * TournamentWaitlist - Full page waitlist management
 */
function TournamentWaitlist() {
  const { tournamentId } = useParams();
  const { tournament } = useOutletContext();

  const [waitlist, setWaitlist] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promoting, setPromoting] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchWaitlist = useCallback(async () => {
    if (!tournamentId) return;

    try {
      setLoading(true);
      const [waitlistRes, statsRes] = await Promise.all([
        waitlistAPI.getWaitlist(tournamentId),
        waitlistAPI.getWaitlistStats(tournamentId)
      ]);
      setWaitlist(waitlistRes.data.waitlist || []);
      setStats(statsRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching waitlist:', err);
      setError('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    fetchWaitlist();
  }, [fetchWaitlist]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = waitlist.findIndex((p) => p._id === active.id);
      const newIndex = waitlist.findIndex((p) => p._id === over.id);

      const newOrder = arrayMove(waitlist, oldIndex, newIndex);
      setWaitlist(newOrder.map((p, idx) => ({ ...p, position: idx + 1 })));

      try {
        await waitlistAPI.reorderWaitlist(
          tournamentId,
          newOrder.map((p) => p._id)
        );
      } catch (err) {
        console.error('Error reordering waitlist:', err);
        fetchWaitlist();
      }
    }
  };

  const handlePromote = async (player) => {
    if (promoting) return;

    try {
      setPromoting(player._id);
      await waitlistAPI.promoteFromWaitlist(
        tournamentId,
        player._id,
        'Promoted by tournament director'
      );

      setWaitlist((prev) =>
        prev
          .filter((p) => p._id !== player._id)
          .map((p, idx) => ({ ...p, position: idx + 1 }))
      );

      // Refresh stats
      const statsRes = await waitlistAPI.getWaitlistStats(tournamentId);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error promoting player:', err);
      setError('Failed to promote player');
    } finally {
      setPromoting(null);
    }
  };

  const handleRemove = async (player) => {
    if (!window.confirm(`Remove ${player.name} from the waitlist?`)) return;

    try {
      await waitlistAPI.removeFromWaitlist(
        tournamentId,
        player._id,
        'Removed by tournament director'
      );

      setWaitlist((prev) =>
        prev
          .filter((p) => p._id !== player._id)
          .map((p, idx) => ({ ...p, position: idx + 1 }))
      );

      const statsRes = await waitlistAPI.getWaitlistStats(tournamentId);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error removing from waitlist:', err);
      setError('Failed to remove player');
    }
  };

  if (loading) {
    return (
      <div className="waitlist-page loading">
        <div className="loading-spinner">Loading waitlist...</div>
      </div>
    );
  }

  return (
    <div className="waitlist-page">
      <div className="waitlist-header">
        <div className="header-left">
          <h2>Waitlist Management</h2>
          <p className="header-subtitle">
            {waitlist.length} player{waitlist.length !== 1 ? 's' : ''} waiting
          </p>
        </div>
        <div className="header-right">
          <button className="refresh-btn" onClick={fetchWaitlist}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchWaitlist}>Retry</button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.rosterSummary?.player || 0}</div>
            <div className="stat-label">Active Players</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-value">{stats.rosterSummary?.waitlisted || 0}</div>
            <div className="stat-label">On Waitlist</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.rosterSummary?.late_drop || 0}</div>
            <div className="stat-label">Late Drops</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.capacity?.expected || '—'}</div>
            <div className="stat-label">Field Capacity</div>
          </div>
        </div>
      )}

      <div className="waitlist-info">
        <p>
          <strong>Priority Order:</strong> Order of Merit rank (lower = higher priority),
          then signup time as tiebreaker.
        </p>
        <p>Drag players to manually reorder. Click ✓ to promote to roster.</p>
      </div>

      {waitlist.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <h3>No Players on Waitlist</h3>
          <p>All signed up players are on the active roster.</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={waitlist.map((p) => p._id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="waitlist-table">
              <div className="table-header">
                <div className="col-position">#</div>
                <div className="col-name">Player</div>
                <div className="col-oom">OOM Rank</div>
                <div className="col-handicap">HCP</div>
                <div className="col-signup">Signup Date</div>
                <div className="col-fairness">Season Stats</div>
                <div className="col-actions">Actions</div>
              </div>
              <div className="table-body">
                {waitlist.map((player) => (
                  <SortableRow
                    key={player._id}
                    player={player}
                    onPromote={() => handlePromote(player)}
                    onRemove={() => handleRemove(player)}
                    isPromoting={promoting === player._id}
                  />
                ))}
              </div>
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function SortableRow({ player, onPromote, onRemove, isPromoting }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: player._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const seasonCount = player.memberWaitlistStats?.thisSeasonWaitlistCount || 0;
  const showFairnessWarning = seasonCount >= 2;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`table-row ${isDragging ? 'dragging' : ''}`}
    >
      <div className="col-position" {...attributes} {...listeners}>
        <span className="position-badge">#{player.position || '—'}</span>
        <span className="drag-handle">⋮⋮</span>
      </div>
      <div className="col-name">
        <div className="player-name">{player.name}</div>
        <div className="player-ghin">GHIN: {player.ghin}</div>
      </div>
      <div className="col-oom">
        {player.oomRank || '—'}
      </div>
      <div className="col-handicap">
        {player.handicapIndex?.toFixed(1) || '—'}
      </div>
      <div className="col-signup">
        {player.signupTimestamp
          ? new Date(player.signupTimestamp).toLocaleDateString()
          : '—'}
      </div>
      <div className="col-fairness">
        {showFairnessWarning ? (
          <span className="fairness-warning" title={`Waitlisted ${seasonCount} times this season`}>
            ⚠️ {seasonCount}x this season
          </span>
        ) : (
          <span className="fairness-ok">
            {seasonCount === 0 ? 'First time' : `${seasonCount}x`}
          </span>
        )}
      </div>
      <div className="col-actions">
        <button
          className="action-btn promote"
          onClick={onPromote}
          disabled={isPromoting}
          title="Promote to roster"
        >
          {isPromoting ? '...' : '✓ Promote'}
        </button>
        <button
          className="action-btn remove"
          onClick={onRemove}
          title="Remove from waitlist"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default TournamentWaitlist;
